import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import { STATUS_META, STATUSES, ENTITY_META, VISIT_DEFAULT } from "@/lib/constants";
import { parseAddress, mapSearchUrl } from "@/lib/address";
import koordinatlar from "@/data/koordinatlar.json";

// İnegöl merkezi — hiçbir üye yerleştirilemezse harita buraya bakar
const INEGOL = [40.0781, 29.5133];

// "Belirsiz"in kart rengi beyaz; açık renkli harita üstünde kayboluyor.
// Harita pinlerinde onun yerine okunur bir gri kullanılıyor.
const PIN_RENGI = { Beyaz: "#6B7280" };
const pinRengi = (durum, varsayilan) => PIN_RENGI[durum] || varsayilan;

/**
 * Aynı noktaya düşen üyeleri üst üste bindirmeden serper.
 * Rastgele değil, altın açıya göre: aynı üye her açılışta aynı yerde durur.
 *
 * Adım büyüklüğü koordinatın kesinliğine göre değişir: sokak koordinatı varsa
 * birkaç on metre, yalnızca mahalle merkezi varsa mahalleye yayılacak kadar
 * geniş — yoksa yüzlerce pin tek noktada yığılıyor.
 */
function serpistir([lat, lon], sira, kesin) {
  if (sira === 0) return [lat, lon];
  const aci = (sira * 137.5 * Math.PI) / 180; // altın açı — düzgün yayılır
  const adim = kesin ? 0.00018 : 0.0011;
  const yaricap = adim * Math.sqrt(sira);
  // enlem arttıkça boylam dereceleri daralır; daire daire kalsın
  const boylamDuzeltme = 1 / Math.cos((lat * Math.PI) / 180);
  return [lat + yaricap * Math.cos(aci), lon + yaricap * Math.sin(aci) * boylamDuzeltme];
}

/** Üyeye koordinat atar: önce sokak, olmazsa mahalle, o da yoksa dışarıda kalır. */
export function uyeKoordinatlari(members) {
  const sayac = new Map();
  const cikti = [];
  for (const m of members) {
    const { mahalle, sokak } = parseAddress(m.address);
    const sokakAnahtar = mahalle && sokak ? `${mahalle}|${sokak}` : null;
    const taban =
      (sokakAnahtar && koordinatlar[sokakAnahtar]) || (mahalle && koordinatlar[mahalle]) || null;
    if (!taban) continue;
    const kesin = Boolean(sokakAnahtar && koordinatlar[sokakAnahtar]);
    const anahtar = kesin ? sokakAnahtar : mahalle;
    const sira = sayac.get(anahtar) || 0;
    sayac.set(anahtar, sira + 1);
    cikti.push({ uye: m, konum: serpistir(taban, sira, kesin), kesin, mahalle, sokak });
  }
  return cikti;
}

/**
 * Balon içeriği DOM olarak kuruluyor; üye adı, firma ve adres textContent ile
 * yazılıyor, HTML olarak birleştirilmiyor.
 */
function balonIcerigi(nokta, onOpenDetail) {
  const { uye, kesin } = nokta;
  const meta = STATUS_META[uye.status] || STATUS_META.Beyaz;
  const em = ENTITY_META[uye.entity_type] || ENTITY_META.Firma;
  const telefon = (uye.phone || "").split("\n")[0]?.split(" - ")[0]?.replace(/[^\d+]/g, "");

  const kok = document.createElement("div");
  kok.style.minWidth = "10rem";

  const ad = document.createElement("div");
  ad.textContent = uye.name;
  ad.style.cssText = "font-weight:600;font-size:13px;line-height:1.25";
  kok.append(ad);

  if (uye.company && uye.company !== uye.name) {
    const firma = document.createElement("div");
    firma.textContent = uye.company;
    firma.style.cssText = "font-size:11px;color:#6b7280;margin-top:2px;line-height:1.35";
    kok.append(firma);
  }

  const rozetler = document.createElement("div");
  rozetler.style.cssText = "display:flex;gap:6px;margin-top:6px";
  const rozet = (metin, arka, yazi) => {
    const s = document.createElement("span");
    s.textContent = metin;
    s.style.cssText =
      `font-size:9px;text-transform:uppercase;letter-spacing:.05em;font-weight:700;` +
      `padding:2px 6px;border-radius:4px;background:${arka};color:${yazi}`;
    return s;
  };
  rozetler.append(rozet(meta.short, pinRengi(uye.status, meta.dot), "#fff"));
  rozetler.append(rozet(em.short, "#e5e7eb", "#374151"));
  kok.append(rozetler);

  const adres = document.createElement("div");
  adres.textContent = uye.address || "";
  adres.style.cssText = "font-size:10.5px;color:#6b7280;margin-top:6px;line-height:1.35";
  kok.append(adres);

  if (!kesin) {
    const uyari = document.createElement("div");
    uyari.textContent = "Sokak bulunamadı — mahalle merkezine yerleştirildi";
    uyari.style.cssText = "font-size:10px;color:#d97706;margin-top:4px";
    kok.append(uyari);
  }

  const eylemler = document.createElement("div");
  eylemler.style.cssText = "display:flex;gap:10px;margin-top:8px;align-items:center";
  const baglanti = (metin, href) => {
    const a = document.createElement("a");
    a.textContent = metin;
    a.href = href;
    a.target = "_blank";
    a.rel = "noreferrer";
    a.style.cssText = "font-size:11px;font-weight:500;color:#0a0a0a;text-decoration:underline";
    return a;
  };
  eylemler.append(baglanti("Yol tarifi", mapSearchUrl(uye.address)));
  if (telefon) eylemler.append(baglanti("Ara", `tel:${telefon}`));
  const kartDugmesi = document.createElement("button");
  kartDugmesi.type = "button";
  kartDugmesi.textContent = "Kart";
  kartDugmesi.style.cssText =
    "font-size:11px;font-weight:500;color:#0a0a0a;text-decoration:underline;" +
    "background:none;border:0;padding:0;cursor:pointer";
  kartDugmesi.addEventListener("click", () => onOpenDetail?.(uye));
  eylemler.append(kartDugmesi);
  kok.append(eylemler);

  return kok;
}

/**
 * Yakın pinleri tek balonda toplar. Balonun rengi içindeki "çaya gidilecek"
 * oranını gösterir: turuncu = burada çok iş var, yeşil = burası bizde.
 */
function KumeKatmani({ noktalar, onOpenDetail }) {
  const harita = useMap();
  const grupRef = useRef(null);
  const ilkOturmaRef = useRef(false);

  // Balondaki "Kart" düğmesi setDetail çağırıyor, o da üst bileşeni yeniden
  // çizip onOpenDetail'in kimliğini değiştiriyordu. Bağımlılıkta dursaydı katman
  // her kart açılışında baştan kurulup harita yakınlaştırması sıfırlanırdı.
  const detayRef = useRef(onOpenDetail);
  useEffect(() => {
    detayRef.current = onOpenDetail;
  }, [onOpenDetail]);

  useEffect(() => {
    const grup = L.markerClusterGroup({
      maxClusterRadius: 45,
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      disableClusteringAtZoom: 17,
      iconCreateFunction: (kume) => {
        const cocuklar = kume.getAllChildMarkers();
        const toplam = cocuklar.length;
        const gidilecek = cocuklar.filter((c) => c.options.gidilecek).length;
        const oran = toplam > 0 ? gidilecek / toplam : 0;
        // Eşikler hedef tanımına göre ayarlı. Karşı taraftakiler hedeften
        // çıkınca genel oran %26'ya düştü; eski 0.66/0.33 eşikleriyle harita
        // neredeyse tümüyle yeşile dönüp ayırt ediciliğini kaybediyordu.
        const renk = oran > 0.4 ? "#F97316" : oran > 0.2 ? "#FBBF24" : "#10B981";
        const cap = toplam > 100 ? 52 : toplam > 30 ? 44 : 36;
        return L.divIcon({
          html:
            `<div style="width:${cap}px;height:${cap}px;border-radius:9999px;` +
            `background:${renk};opacity:.9;border:2px solid #fff;` +
            `box-shadow:0 1px 6px rgba(0,0,0,.3);display:flex;align-items:center;` +
            `justify-content:center;color:#fff;font-weight:700;` +
            `font-size:${toplam > 100 ? 14 : 12}px;` +
            `font-variant-numeric:tabular-nums">${toplam}</div>`,
          className: "rota-kume",
          iconSize: [cap, cap],
        });
      },
    });

    for (const nokta of noktalar) {
      const { uye, konum } = nokta;
      const meta = STATUS_META[uye.status] || STATUS_META.Beyaz;
      const gidilecek = VISIT_DEFAULT.includes(uye.status);
      const isaret = L.circleMarker(konum, {
        radius: gidilecek ? 6 : 4,
        color: "#ffffff",
        weight: gidilecek ? 1.5 : 1,
        fillColor: pinRengi(uye.status, meta.dot),
        fillOpacity: gidilecek ? 0.9 : 0.45,
        gidilecek, // küme rengi hesaplanırken okunuyor
      });
      isaret.bindPopup(() => balonIcerigi(nokta, (u) => detayRef.current?.(u)));
      grup.addLayer(isaret);
    }

    harita.addLayer(grup);
    grupRef.current = grup;

    // Yalnızca ilk yüklemede kapsama otur. Süzgeç açılıp kapandığında haritayı
    // yeniden çerçevelemek, kullanıcının yakınlaştırdığı yeri kaybettiriyor.
    if (noktalar.length > 0 && !ilkOturmaRef.current) {
      ilkOturmaRef.current = true;
      harita.fitBounds(
        noktalar.map((n) => n.konum),
        { padding: [28, 28], maxZoom: 16 }
      );
    }

    return () => {
      harita.removeLayer(grup);
      grupRef.current = null;
    };
  }, [noktalar, harita]);

  return null;
}

export default function RouteMap({ members, onOpenDetail }) {
  const [gizli, setGizli] = useState(() => new Set());

  const tumNoktalar = useMemo(() => uyeKoordinatlari(members), [members]);
  const noktalar = useMemo(
    () => tumNoktalar.filter((n) => !gizli.has(n.uye.status)),
    [tumNoktalar, gizli]
  );
  const eksik = members.length - tumNoktalar.length;

  const degistir = (durum) =>
    setGizli((onceki) => {
      const yeni = new Set(onceki);
      if (yeni.has(durum)) yeni.delete(durum);
      else yeni.add(durum);
      return yeni;
    });

  return (
    <div
      className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden"
      data-testid="route-map"
    >
      <div className="h-[300px] sm:h-[440px] w-full">
        <MapContainer
          center={INEGOL}
          zoom={13}
          scrollWheelZoom={false}
          className="h-full w-full"
          style={{ background: "#e5e7eb" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
          />
          <KumeKatmani noktalar={noktalar} onOpenDetail={onOpenDetail} />
        </MapContainer>
      </div>

      {/* Renk anahtarı — aynı zamanda göster/gizle düğmeleri */}
      <div className="border-t border-neutral-100 dark:border-neutral-800 px-3 py-2.5 flex flex-wrap items-center gap-1.5">
        {STATUSES.map((s) => {
          const meta = STATUS_META[s];
          const kapali = gizli.has(s);
          const adet = tumNoktalar.filter((n) => n.uye.status === s).length;
          return (
            <button
              key={s}
              type="button"
              onClick={() => degistir(s)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10.5px] font-medium transition-colors ${
                kapali
                  ? "border-neutral-200 dark:border-neutral-800 text-neutral-400 dark:text-neutral-600"
                  : "border-neutral-300 dark:border-neutral-700 text-neutral-800 dark:text-neutral-100"
              }`}
              data-testid={`map-toggle-${meta.key}`}
            >
              <span
                className="h-2.5 w-2.5 rounded-full shrink-0"
                style={{ background: pinRengi(s, meta.dot), opacity: kapali ? 0.25 : 1 }}
              />
              {meta.short}
              <span className="font-mono text-neutral-400">{adet}</span>
            </button>
          );
        })}
        {eksik > 0 && (
          <span className="text-[10px] text-neutral-400 dark:text-neutral-500 ml-auto">
            {eksik} üye haritaya yerleştirilemedi
          </span>
        )}
      </div>

      <div className="border-t border-neutral-100 dark:border-neutral-800 px-3 py-2 text-[10px] text-neutral-500 dark:text-neutral-400">
        Balonlardaki sayı o bölgedeki üye adedi; rengi çaya gidilecek oranını gösterir
        (<span className="font-semibold" style={{ color: "#F97316" }}>turuncu</span> = iş çok,{" "}
        <span className="font-semibold" style={{ color: "#10B981" }}>yeşil</span> = burası bizde).
        Yakınlaştırınca balonlar tek tek üyelere ayrılır.
      </div>
    </div>
  );
}
